/**
 * PlaybackPage - デッキ再生画面コンポーネント（簡素化版）
 * 3つの機能のみ：大きなナビゲーションボタン、進捗表示、ピンチズーム
 */

import { useEffect } from 'react'
import { useStore, usePlaybackData } from '../store'
import { VirtualizedImageList } from '../features/playback/components/VirtualizedImageList'
import { ProgressIndicator } from '../features/playback/components/ProgressIndicator'
import { logger } from '../lib/logger'

export function PlaybackPage() {
  // Get computed data from selector with shallow comparison
  const { status, currentSet, setItems } = usePlaybackData()
  
  // Debug logging
  useEffect(() => {
    logger.log('[PlaybackPage] Component mounted')
    logger.log('[PlaybackPage] Status:', status)
    logger.log('[PlaybackPage] CurrentSet:', currentSet)
    logger.log('[PlaybackPage] SetItems count:', setItems.length)
  }, [status, currentSet, setItems])
  
  // Navigation and playback controls
  const navigateTo = useStore(state => state.navigateTo)
  const currentIndex = useStore(state => state.viewState.playback.currentIndex)
  const nextImage = useStore(state => state.nextImage)
  const previousImage = useStore(state => state.previousImage)

  // Browser back button handling
  useEffect(() => {
    // Push a new history entry when entering playback mode
    window.history.pushState({ page: 'playback' }, '', null)
    
    const handlePopState = (e: PopStateEvent) => {
      // When back button is pressed, navigate to gallery
      e.preventDefault()
      navigateTo('decks')
    }
    
    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [navigateTo])

  // Escapeキーでデッキに戻る（最小限のキーボードショートカット）
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigateTo('decks')
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [navigateTo])

  // Render based on status
  
  // Loading state
  if (status === 'loading') {
    logger.log('[PlaybackPage] Rendering loading state')
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-500">読み込み中...</p>
        </div>
      </div>
    )
  }
  
  // Error state
  if (status === 'error') {
    logger.log('[PlaybackPage] Rendering error state')
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 mb-4">セットの読み込みに失敗しました</p>
          <button
            onClick={() => navigateTo('decks')}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
          >
            デッキに戻る
          </button>
        </div>
      </div>
    )
  }
  
  // Empty state
  if (status === 'empty') {
    logger.log('[PlaybackPage] Rendering empty state')
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 mb-4">表示する画像がありません</p>
          <button
            onClick={() => navigateTo('capture')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors mb-2"
          >
            撮影を開始
          </button>
          <button
            onClick={() => navigateTo('decks')}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
          >
            デッキに戻る
          </button>
        </div>
      </div>
    )
  }
  
  // Success state - at this point, currentSet and setItems are guaranteed to be valid
  if (status !== 'success' || !currentSet || setItems.length === 0) {
    // Unexpected state (should not happen)
    logger.error('[PlaybackPage] Unexpected state:', status, currentSet, setItems)
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <p className="text-gray-500">エラーが発生しました</p>
        <button
          onClick={() => navigateTo('home')}
          className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
        >
          ホームに戻る
        </button>
      </div>
    )
  }
  
  logger.log('[PlaybackPage] Rendering success state - set:', currentSet.id, 'items:', setItems.length)

  return (
    <div 
      className="bg-black text-white flex flex-col"
      style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
    >
      {/* Header - fixed height */}
      <div className="flex-shrink-0 flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-black/80 to-transparent">
        <button
          onClick={() => navigateTo('decks')}
          className="p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg transition-colors"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        
        <h2 className="text-lg font-semibold">
          {currentSet.name || 'デッキ'}
        </h2>
        
        <div className="w-10"></div>
      </div>
      
      {/* Virtualized Image List - constrained height for mobile */}
      <div className="flex-1 relative overflow-hidden" style={{ maxHeight: '60vh' }}>
        <VirtualizedImageList
          items={setItems}
          currentIndex={currentIndex}
          setType={currentSet.type}
          currentSet={{
            type: currentSet.type,
            id: currentSet.id,
            name: currentSet.name || 'Untitled Set'
          }}
        />
      </div>
      
      {/* シンプルな進捗表示と大きなナビゲーションボタン - 縦画面用最適化 */}
      <div className="flex-shrink-0 bg-gradient-to-t from-black via-black/80 to-transparent pb-safe">
        <div className="p-4">
          {/* 読み取り専用の進捗表示 */}
          <ProgressIndicator
            current={currentIndex}
            total={setItems.length}
            isCircular={currentSet.type === 'circular'}
          />
          
          {/* 大きなナビゲーションボタン（縦画面用） */}
          <div className="flex items-center justify-center gap-8 mt-6">
            {/* 前へボタン - シャッターサイズ相当 */}
            <button
              onClick={() => {
                logger.log('[PlaybackPage] Previous button clicked - currentIndex:', currentIndex)
                previousImage()
              }}
              className="w-20 h-20 bg-gray-800/90 hover:bg-gray-700/90 rounded-full transition-all transform active:scale-95 flex items-center justify-center"
              disabled={currentIndex === 0 && currentSet.type !== 'circular'}
              aria-label="前の画像"
            >
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* 次へボタン - シャッターサイズ相当 */}
            <button
              onClick={() => {
                logger.log('[PlaybackPage] Next button clicked - currentIndex:', currentIndex)
                nextImage()
              }}
              className="w-20 h-20 bg-blue-600/90 hover:bg-blue-500/90 rounded-full transition-all transform active:scale-95 flex items-center justify-center"
              disabled={currentIndex === setItems.length - 1 && currentSet.type !== 'circular'}
              aria-label="次の画像"
            >
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}